import { z } from "zod";

import { nullable } from "../globals.types";

const programAcuteVariablesSchema = z.object({
	minCycleLength: nullable(
		z.number().int().positive().describe("Minimum cycle/phase length in weeks"),
	),
	maxCycleLength: nullable(
		z.number().int().positive().describe("Maximum cycle/phase length in weeks"),
	),
	minFrequency: nullable(
		z.number().int().positive().describe("Minimum training frequency per week"),
	),
	maxFrequency: nullable(
		z.number().int().positive().describe("Maximum training frequency per week"),
	),

	minTrainingTime: z
		.number()
		.int()
		.positive()
		.describe("Minimum session duration in minutes"),
	maxTrainingTime: z
		.number()
		.int()
		.positive()
		.describe("Maximum session duration in minutes"),
	minLoad: z
		.number()
		.int()
		.nonnegative()
		.max(100)
		.describe("Minimum load as percentage of 1RM (0-100)"),
	maxLoad: z
		.number()
		.int()
		.nonnegative()
		.max(100)
		.describe("Maximum load as percentage of 1RM (0-100)"),
	minReps: z.number().int().positive().describe("Minimum reps per set"),
	maxReps: z.number().int().positive().describe("Maximum reps per set"),
	repsToFailure: z
		.boolean()
		.default(false)
		.describe("Whether sets should be taken to failure"),
	loadingStyle: z
		.enum(["vertical", "horizontal"])
		.describe(
			"Loading style: horizontal = traditional sets/reps, vertical = circuit training",
		)
		.default("horizontal"),
	// Horizontal loading
	minSets: nullable(
		z
			.number()
			.int()
			.positive()
			.describe("Minimum sets per exercise (horizontal loading only)"),
	),
	maxSets: nullable(
		z
			.number()
			.int()
			.positive()
			.describe("Maximum sets per exercise (horizontal loading only)"),
	),
	restBetweenSets: nullable(
		z
			.number()
			.int()
			.nonnegative()
			.describe("Rest between sets in seconds (horizontal loading only)"),
	),
	// Vertical loading
	minCircuits: nullable(
		z
			.number()
			.int()
			.positive()
			.describe("Minimum circuits per routine (vertical loading only)"),
	),
	maxCircuits: nullable(
		z
			.number()
			.int()
			.positive()
			.describe("Maximum circuits per routine (vertical loading only)"),
	),
	restBetweenExercises: nullable(
		z
			.number()
			.int()
			.nonnegative()
			.describe("Rest between exercises in seconds (vertical loading only)"),
	),
	restBetweenCircuits: nullable(
		z
			.number()
			.int()
			.nonnegative()
			.describe("Rest between circuits in seconds (vertical loading only)"),
	),
	// Tempo
	tempoEccentric: z
		.number()
		.int()
		.gte(-1)
		.lte(4)
		.describe("Eccentric tempo in seconds: 0, 1, 2, 3, 4, or -1 (explosive)"),
	tempoIsometric: z
		.number()
		.int()
		.gte(0)
		.lte(4)
		.describe("Isometric hold tempo in seconds: 0, 1, 2, 3, or 4"),
	tempoConcentric: z
		.number()
		.int()
		.gte(-2)
		.lte(2)
		.describe(
			"Concentric tempo in seconds: 0, 1, 2, -1 (explosive), or -2 (max velocity)",
		),
	setStrategy: z
		.enum(["standard", "drop_sets", "strength_stability_dropsets", "pap"])
		.default("standard")
		.describe(
			"Set strategy: standard, drop sets, strength-stability drop sets, or post-activation potentiation",
		),
});

const exerciseItemSchema = z.object({
	isActiveRest: z.boolean().describe("Whether the exercise is an active rest"),
	orderIndex: z.number().int().nonnegative(),
	id: z.string().describe("Exercise ID from the exercise library"),
	progressionExerciseId: z
		.string()
		.nullable()
		.describe("Progression exercise ID from the exercise library"),
});

const phaseRoutineFullBodySchema = z.object({
	splitType: z.literal("full_body"),
	exercises: z.array(exerciseItemSchema),
});

const phaseRoutineUpperLowerSchema = z.object({
	splitType: z.literal("upper_lower"),
	upperBodyExercises: z.array(exerciseItemSchema),
	lowerBodyExercises: z.array(exerciseItemSchema),
});

const phaseRoutinePushPullLegsSchema = z.object({
	splitType: z.literal("push_pull_legs"),
	pushExercises: z.array(exerciseItemSchema),
	pullExercises: z.array(exerciseItemSchema),
	legExercises: z.array(exerciseItemSchema),
});

const phaseRoutineSchema = z.discriminatedUnion("splitType", [
	phaseRoutineFullBodySchema,
	phaseRoutineUpperLowerSchema,
	phaseRoutinePushPullLegsSchema,
]);

export const warmupRoutineOutputSchema = z.object({
	movementDysfunctionFocus: z.string(),
	exercises: z.array(
		z.object({
			orderIndex: z.number().int().nonnegative(),
			id: z.string().describe("Exercise ID from the exercise library"),
		}),
	),
});

export type WarmupRoutineOutput = z.infer<typeof warmupRoutineOutputSchema>;

export const strengthTrainingProgramOutputSchema = z.object({
	name: z.string(),
	goal: z.string(),
	experience: z.enum(["beginner", "intermediate", "advanced"]),

	phases: z
		.array(
			z.object({
				name: z.string(),
				orderIndex: z.number().int().nonnegative(),
				acuteVariables: programAcuteVariablesSchema,
				warmupRoutine: warmupRoutineOutputSchema.nullable(),
				lowerWarmupRoutine: warmupRoutineOutputSchema.nullable(),
				upperWarmupRoutine: warmupRoutineOutputSchema.nullable(),
				mainRoutine: phaseRoutineSchema,
				undulationRoutine: phaseRoutineSchema.nullable(),
			}),
		)
		.min(1),
});

export type StrengthTrainingProgramOutput = z.infer<
	typeof strengthTrainingProgramOutputSchema
>;

export const strengthTrainingProgramPlanSchema = z.object({
	name: z.string(),
	goal: z.string(),
	experience: z.enum(["beginner", "intermediate", "advanced"]),
	phases: z
		.array(
			z.object({
				name: z.string(),
				orderIndex: z.number().int().nonnegative(),
				acuteVariables: programAcuteVariablesSchema,
				mainRoutine: z.object({
					splitType: z.enum(["full_body", "upper_lower", "push_pull_legs"]),
				}),
				undulationRoutine: z
					.object({
						splitType: z.enum(["full_body", "upper_lower", "push_pull_legs"]),
					})
					.nullable(),
			}),
		)
		.min(1),
});

export type StrengthTrainingProgramPlan = z.infer<
	typeof strengthTrainingProgramPlanSchema
>;

export const strengthTrainingPhaseExercisesSchemas = {
	full_body: z.object({
		mainRoutine: phaseRoutineFullBodySchema,
		undulationRoutine: phaseRoutineFullBodySchema.nullable(),
	}),
	upper_lower: z.object({
		mainRoutine: phaseRoutineUpperLowerSchema,
		undulationRoutine: phaseRoutineUpperLowerSchema.nullable(),
	}),
	push_pull_legs: z.object({
		mainRoutine: phaseRoutinePushPullLegsSchema,
		undulationRoutine: phaseRoutinePushPullLegsSchema.nullable(),
	}),
} as const;

export type StrengthTrainingPhaseExercises = z.infer<
	(typeof strengthTrainingPhaseExercisesSchemas)[keyof typeof strengthTrainingPhaseExercisesSchemas]
>;

export const clinicalProgramOutputSchema = z.object({
	name: z.string(),
	goal: z.string(),

	commonComplaints: z.array(z.string()),
	hypothesizedChanges: z
		.array(
			z.object({
				treatmentSummary: nullable(
					z.string().describe("Summary of the hypothesized treatment approach"),
				),
				laterality: z
					.enum(["bilateral", "right", "left"])
					.describe("Laterality of the condition or treatment"),
				overactiveShort: z
					.array(z.string())
					.describe("List of overactive and short muscles"),
				overactiveLong: z
					.array(z.string())
					.describe("List of overactive and long muscles"),
				underactiveLong: z
					.array(z.string())
					.describe("List of underactive and long muscles"),
				underactiveShort: z
					.array(z.string())
					.describe("List of underactive and short muscles"),
				stiffJoints: z
					.array(z.string())
					.describe("List of stiff or restricted joints"),
			}),
		)
		.min(1),

	mobilityRoutine: z.object({
		exercises: z.array(
			z.object({
				orderIndex: z.number().int().nonnegative(),
				id: z
					.string()
					.describe("Exercise ID from the mobility exercise library"),
				laterality: z
					.enum(["bilateral", "right", "left"])
					.describe("Laterality of the exercise"),
			}),
		),
	}),

	activationIntegrationRoutine: z.object({
		acuteVariables: programAcuteVariablesSchema,
		exercises: z.array(
			z.object({
				orderIndex: z.number().int().nonnegative(),
				id: z
					.string()
					.describe(
						"Exercise ID from the activation/integration exercise library",
					),
				laterality: z
					.enum(["bilateral", "right", "left"])
					.describe("Laterality of the exercise"),
			}),
		),
	}),

	homeExerciseRoutine: z.object({
		acuteVariables: programAcuteVariablesSchema,
		exercises: z.array(
			z.object({
				orderIndex: z.number().int().nonnegative(),
				id: z.string().describe("Exercise ID from the home exercise library"),
				laterality: z
					.enum(["bilateral", "right", "left"])
					.describe("Laterality of the exercise"),
			}),
		),
	}),
});

export type ClinicalProgramOutput = z.infer<typeof clinicalProgramOutputSchema>;

export type ProgramAcuteVariablesOutput = z.infer<
	typeof programAcuteVariablesSchema
>;
